import { useState } from "react";

import {
  auditStamp,
  blockedIpsOf,
  liveWithinSecondsOf,
  WAF_BLOCKED_IPS_PATH,
  type McpBlockedIps,
  type McpBlockedIpWrite,
} from "../../../api/mcp";
import { Gate } from "../../../components/authority/Gate";
import { ResourceView } from "../../../components/states";
import { Card, Empty, Table, Td, Th } from "../../../components/ui";
import { ConfirmDialog } from "../../../components/write/ConfirmDialog";
import { effectFromSeconds, WriteResult } from "../../../components/write/EffectTiming";
import { WriteFailure } from "../../../components/write/WriteFailure";
import type { AppConfig } from "../../../config";
import { Button, Input } from "../../../f7";
import { useResource } from "../../../hooks/useResource";
import { useWrite } from "../../../hooks/useWrite";

type Pending = { action: "block" | "unblock"; cidr: string };

/**
 * docs/products/mcp.md › "Blocked IPs (WAF)".
 *
 * The widest block there is: the WAF refuses the address on EVERY path of the
 * MCP server, sign-in included, whoever is behind it. That is why blocking is
 * L3 (type the CIDR) and the dialog says a shared provider IP can be many
 * merchants. Unblocking is the way back, so it is L2 — but the API still
 * requires a reason, so the unblock is attributed too.
 *
 * One entry per write, unlike paused tools: POST adds a CIDR, DELETE removes
 * one. MCP validates the address (too wide, private, a protected AI-platform
 * range); its refusal is shown as-is rather than guessed at here.
 */
export function BlockedIpsPanel({ config }: { config: AppConfig }) {
  const list = useResource<McpBlockedIps>(config, WAF_BLOCKED_IPS_PATH);
  const write = useWrite<McpBlockedIpWrite>(config);
  const [cidr, setCidr] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const [result, setResult] = useState<(Pending & { seconds?: number }) | null>(null);

  const typed = cidr.trim();
  const seconds = list.state.kind === "ready" ? liveWithinSecondsOf(list.state.data) : undefined;

  function submit(reason: string) {
    if (!pending) return;
    const done = pending;
    void write
      .submit(WAF_BLOCKED_IPS_PATH, {
        method: done.action === "block" ? "POST" : "DELETE",
        body: JSON.stringify({ cidr: done.cidr, reason }),
      })
      .then((r) => {
        if (!r) return;
        setResult({ ...done, seconds: liveWithinSecondsOf(r) });
        setPending(null);
        if (done.action === "block") setCidr("");
        // Re-read rather than trusting the response: a lost reply is
        // indistinguishable from a failed write.
        list.reload();
      });
  }

  return (
    <Card
      title="Blocked IPs"
      note="Refused by the WAF on every path, sign-in included, matched on the real client IP. For a client the rate limits only slow down."
    >
      <ResourceView
        state={list.state}
        what="the blocked IPs"
        onRetry={list.reload}
        notFound={<Empty>The gateway does not route the blocked-IP list on this stack.</Empty>}
      >
        {(data) => {
          const rows = blockedIpsOf(data);
          return rows.length === 0 ? (
            <Empty>No IP is blocked.</Empty>
          ) : (
            <Table
              className="[--gutter:0px]"
              head={
                <tr>
                  <Th>Address</Th>
                  <Th>Last change</Th>
                  <Th right>Action</Th>
                </tr>
              }
            >
              {rows.map((row) => (
                <tr key={row.cidr}>
                  <Td>
                    <span className="ref">{row.cidr}</span>
                  </Td>
                  <Td>
                    {row.lastChange ? (
                      <div className="text-xs/5">
                        <div>{row.lastChange.reason ?? "no reason recorded"}</div>
                        <div className="text-slate-500 dark:text-slate-400">
                          {row.lastChange.actor ?? "unknown actor"} · {auditStamp(row.lastChange.at)}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs/5 text-slate-500 dark:text-slate-400">
                        No audit row for this address.
                      </span>
                    )}
                  </Td>
                  <Td right>
                    <Gate need="settings:write">
                      <Button outline onClick={() => setPending({ action: "unblock", cidr: row.cidr })}>
                        Unblock
                      </Button>
                    </Gate>
                  </Td>
                </tr>
              ))}
            </Table>
          );
        }}
      </ResourceView>

      {list.state.kind === "ready" && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="w-64">
            <Input
              value={cidr}
              onChange={(e) => setCidr(e.target.value)}
              placeholder="185.23.4.5 or 185.23.4.0/24"
              aria-label="IP or CIDR to block"
            />
          </div>
          <Gate need="settings:write">
            <Button color="red" disabled={!typed} onClick={() => setPending({ action: "block", cidr: typed })}>
              Block IP
            </Button>
          </Gate>
        </div>
      )}

      <WriteFailure state={write.state} />

      {result && (
        <div className="mt-3">
          <WriteResult
            title={result.action === "block" ? "Blocked" : "Unblocked"}
            onDismiss={() => setResult(null)}
          >
            <p>
              {result.cidr} {result.action === "block" ? "is refused on every path." : "is allowed again."}{" "}
              {effectFromSeconds(result.seconds, "This")}
            </p>
          </WriteResult>
        </div>
      )}

      <ConfirmDialog
        open={pending !== null}
        title={pending?.action === "block" ? `Block ${pending.cidr}` : `Unblock ${pending?.cidr ?? ""}`}
        confirmLabel={pending?.action === "block" ? "Block" : "Unblock"}
        destructive={pending?.action === "block"}
        requireReason
        typeToConfirm={pending?.action === "block" ? pending.cidr : undefined}
        reasonHint="e.g. looping agent, 59k requests in 6 h"
        busy={write.state.kind === "submitting"}
        onCancel={() => setPending(null)}
        onConfirm={submit}
        effect={effectFromSeconds(seconds, "This")}
      >
        {pending?.action === "block" ? (
          <>
            <p>
              Everyone behind {pending.cidr} is refused on every path, sign-in included, signed
              in or not.
            </p>
            <p className="mt-2">
              Hosted assistants send many merchants' calls from a few shared IPs. When the
              problem is one account, block the merchant or user instead.
            </p>
          </>
        ) : (
          <p>{pending?.cidr} is allowed again on every path.</p>
        )}
      </ConfirmDialog>
    </Card>
  );
}
